import { ethers } from 'ethers';
import { 
  PaymentCapsule, 
  CreatePaymentInput,
  PaginationParams,
  PaginatedResponse 
} from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

const LEGACY_PAYMENTS_ABI = [
  'function createPayment(address recipient, uint256 unlockTime, address token, string memory message) public payable returns (uint256)',
  'function claimPayment(uint256 paymentId) public',
  'function refundPayment(uint256 paymentId) public',
  'function getPayment(uint256 paymentId) public view returns (address sender, address recipient, uint256 amount, address token, uint256 unlockTime, bool claimed, string memory message)'
];

export class PaymentService {
  private client: LegacyChainClient;
  private contractAddress: string;

  constructor(client: LegacyChainClient) {
    this.client = client;
    this.contractAddress = process.env.NEXT_PUBLIC_LEGACY_PAYMENTS_ADDRESS || '';
  }

  async create(input: CreatePaymentInput): Promise<PaymentCapsule> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_PAYMENTS_ABI, signer);
    
    let message = input.message || '';
    let encryptionMetadata: any = {};

    // Encrypt message if requested
    if (input.encrypt && input.message) {
      const password = input.password || this.client.crypto.generatePassword();
      const { encrypted, salt, iv } = await this.client.crypto.encrypt(input.message, password);
      
      // Store encrypted message on IPFS
      const ipfsHash = await this.client.ipfs.uploadJSON({
        encrypted,
        salt,
        iv
      }, 'payment-message');
      
      message = `ipfs://${ipfsHash}`;
      encryptionMetadata = { encrypted: true, password };
    }

    const unlockTimestamp = Math.floor(input.unlockTime.getTime() / 1000);
    const value = input.token === 'ETH' 
      ? ethers.parseEther(input.amount)
      : '0';
    
    const tokenAddress = this.getTokenAddress(input.token);
    
    // Approve token if not ETH
    if (input.token !== 'ETH') {
      await this.approveToken(tokenAddress, input.amount);
    }
    
    const tx = await contract.createPayment(
      input.recipient,
      unlockTimestamp,
      tokenAddress,
      message,
      { value }
    );
    
    const receipt = await tx.wait();
    const paymentId = receipt.logs[0].args.paymentId.toString();

    const payment: PaymentCapsule = {
      id: paymentId,
      sender: await signer.getAddress(),
      recipient: input.recipient,
      amount: input.amount,
      token: input.token,
      unlockTime: input.unlockTime,
      status: 'pending',
      message: input.message,
      encrypted: input.encrypt,
      createdAt: new Date()
    };

    // Store in backend
    await this.client.makeRequest('POST', '/payments', payment);
    
    // Emit event
    this.client.emit('payment:created', payment);

    return { ...payment, ...encryptionMetadata };
  }

  async claim(paymentId: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_PAYMENTS_ABI, signer);
    
    const tx = await contract.claimPayment(paymentId);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('PUT', `/payments/${paymentId}/claim`);
    
    // Emit event
    this.client.emit('payment:claimed', paymentId);
  }

  async refund(paymentId: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_PAYMENTS_ABI, signer);
    
    const tx = await contract.refundPayment(paymentId);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('PUT', `/payments/${paymentId}/refund`);
  }

  async get(paymentId: string): Promise<PaymentCapsule> {
    const response = await this.client.makeRequest<PaymentCapsule>('GET', `/payments/${paymentId}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch payment');
    }

    return response.data;
  }

  async list(params?: PaginationParams): Promise<PaginatedResponse<PaymentCapsule>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    const response = await this.client.makeRequest<PaginatedResponse<PaymentCapsule>>(
      'GET',
      `/payments?${queryParams.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch payments');
    }

    return response.data;
  }

  async getBalance(token: 'ETH' | 'USDC' | 'USDT' | 'DAI'): Promise<string> {
    const signer = this.client.getSigner();
    const address = await signer.getAddress();
    
    if (token === 'ETH') {
      const balance = await signer.provider?.getBalance(address);
      return ethers.formatEther(balance || '0');
    }
    
    // For ERC20 tokens
    const tokenAddress = this.getTokenAddress(token);
    const tokenContract = new ethers.Contract(
      tokenAddress,
      ['function balanceOf(address) view returns (uint256)'],
      signer
    );
    
    const balance = await tokenContract.balanceOf(address);
    return ethers.formatUnits(balance, 6); // Assuming 6 decimals for stablecoins
  }

  private getTokenAddress(token: string): string {
    const addresses: Record<string, string> = {
      'ETH': ethers.ZeroAddress,
      'USDC': '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      'USDT': '0xdAC17F958D2ee523a2206206994597C13D831ec7',
      'DAI': '0x6B175474E89094C44Da98b954EedeAC495271d0F'
    };
    
    return addresses[token] || ethers.ZeroAddress;
  }

  private async approveToken(tokenAddress: string, amount: string): Promise<void> {
    const signer = this.client.getSigner();
    const tokenContract = new ethers.Contract(
      tokenAddress,
      ['function approve(address spender, uint256 amount) returns (bool)'],
      signer
    );
    
    const amountInUnits = ethers.parseUnits(amount, 6);
    const tx = await tokenContract.approve(this.contractAddress, amountInUnits);
    await tx.wait();
  }

  async decryptMessage(payment: PaymentCapsule, password: string): Promise<string> {
    if (!payment.encrypted || !payment.message?.startsWith('ipfs://')) {
      return payment.message || '';
    }

    const ipfsHash = payment.message.replace('ipfs://', '');
    const encryptedData = await this.client.ipfs.retrieveJSON<any>(ipfsHash);
    
    return this.client.crypto.decrypt(
      encryptedData.encrypted,
      password,
      encryptedData.salt,
      encryptedData.iv
    );
  }
} 